/**
 * TemplatePreviewModal — E-93 Story 93.4.
 *
 * Opens over the bulk-email page and shows the renderable preview of the
 * selected template for the current language + variables. The preview
 * comes straight from the backend endpoint
 * (`POST /settings/email-templates/:slug/preview`) — what the user sees
 * here is byte-identical to what partners will receive.
 *
 * ## Why an iframe, not dangerouslySetInnerHTML
 *
 * Email templates may contain legacy HTML with aggressive inline styles,
 * `<script>`-shaped text (that isn't actually executable), or tables that
 * break the host page layout. A same-origin sandboxed iframe:
 *
 *   - Isolates the template CSS from our app's Tailwind scope
 *   - Scripts inside the iframe are blocked by `sandbox="allow-same-origin"`
 *     (we deliberately omit `allow-scripts`)
 *   - Gives us a proper "responsive preview" that matches Mail client widths
 *
 * ## Sample data
 *
 * The backend preview endpoint returns `sampleData` alongside the rendered
 * HTML. We surface it below the preview so the user can see which
 * placeholders got filled with what — avoids the "why does it say John
 * Doe?" question on first use.
 */

import { previewEmailTemplate, type PreviewResult } from '@/api/email-templates';
import { useTranslation } from '@kgc/i18n';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@kgc/ui';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';

export interface TemplatePreviewModalProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  /** Slug of the template to render. `null` short-circuits to a friendly empty state. */
  slug: string | null;
  /** Language key (hu/en/de/uk). */
  language: string;
  /** User-entered variable map. */
  variables: Record<string, string>;
}

/**
 * Build the iframe document from the backend-rendered HTML. The wrapper
 * ensures the iframe has sensible defaults (UTF-8 meta, viewport) so
 * emails that omit them still render correctly in the preview.
 */
export function wrapPreviewHtml(html: string): string {
  // The backend may or may not return a complete `<html>` document. If it
  // does, we respect it; if it returns a fragment, we wrap it. A naive
  // `html.includes('<html')` is the right kind of cheap check — emails
  // that inject `<html>` mid-content are pathological and not worth
  // defending against here.
  if (/<html[\s>]/i.test(html)) return html;
  return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<style>body{font-family:system-ui,sans-serif;color:#111;line-height:1.5;padding:16px;margin:0;}</style>
</head>
<body>${html}</body>
</html>`;
}

export function TemplatePreviewModal({
  open,
  onOpenChange,
  slug,
  language,
  variables,
}: TemplatePreviewModalProps): JSX.Element {
  const { t } = useTranslation();
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch on open + on slug/language/variables change while open.
  // We DON'T prefetch — the modal is a click-to-open surface, and the
  // preview call hits the templating pipeline which can be non-trivial.
  useEffect(() => {
    if (!open || !slug) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    previewEmailTemplate(slug, language, variables)
      .then(res => {
        if (cancelled) return;
        setPreview(res.data);
      })
      .catch(err => {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, slug, language, variables]);

  const srcDoc = preview ? wrapPreviewHtml(preview.html) : '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>{t('email.bulkSend.preview.title')}</DialogTitle>
          <DialogDescription>
            {slug
              ? t('email.bulkSend.preview.subtitle', { slug, language })
              : t('email.bulkSend.preview.noTemplate')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {loading && (
            <div className="rounded-md border p-8 text-center text-sm text-muted-foreground">
              {t('email.bulkSend.preview.loading')}
            </div>
          )}
          {error && !loading && (
            <div className="rounded-md border border-destructive p-3 text-sm text-destructive">
              {t('email.bulkSend.preview.error')}: {error}
            </div>
          )}
          {preview && !loading && !error && (
            <>
              <div className="rounded-md border bg-muted/30 p-3 text-sm">
                <span className="font-medium">{t('email.bulkSend.preview.subjectLabel')}:</span>{' '}
                {preview.subject}
              </div>
              <iframe
                // Same-origin so CSS can use the app's font stack; NO
                // allow-scripts — any <script> tag in the template is
                // inert. Title is required for a11y.
                title={t('email.bulkSend.preview.iframeTitle')}
                sandbox="allow-same-origin"
                srcDoc={srcDoc}
                className="h-[60vh] w-full rounded-md border bg-background"
              />
              {Object.keys(preview.sampleData).length > 0 && (
                <details className="rounded-md border p-2 text-xs">
                  <summary className="cursor-pointer font-medium">
                    {t('email.bulkSend.preview.sampleDataLabel')}
                  </summary>
                  <dl className="mt-2 grid grid-cols-2 gap-2">
                    {Object.entries(preview.sampleData).map(([key, value]) => (
                      <div key={key} className="contents">
                        <dt className="font-mono text-muted-foreground">{key}</dt>
                        <dd className="font-mono">{value}</dd>
                      </div>
                    ))}
                  </dl>
                </details>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('email.bulkSend.preview.closeButton')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
